import { Alert, Button, Grid, Typography } from "@mui/material";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { fetchAllotmentValueByStationWorkgroupAndYear } from "../../api/employee.js";
import { validateAllotments } from "../../util/bid/IAMDATBidValidations";
import { isAwardedBid, isClosedBid, isDeletedBid } from "../../util/bid/bidCommon";
import { getEligibleEmployees, getIneligibleEmployees } from "../../util/bid/bidGroupUtils";
import { calcDailyAllotmentInitialState, getTotalAllotments, isHoliday } from "../../util/bid/timeOffBidUtils";
import { getDayStateForIAMDATAllotment } from "../../util/calendar";
import { isObjectEmpty } from "../../util/commonUtils";
import { getThanksGivingDateOfTheYear } from "../../util/dateHelper";
import { convertIsoDateToLuxon } from "../../util/dateTimeFormatter";
import PageLoading from "../PageLoading";
import ConfirmationDialog from "../fundamentals/ConfirmationDialog";
import SuccessDialog from "../fundamentals/SuccessDialog";
import AllotmentsOverrideDialog from "./AllotmentsOverrideDialog";
import IAMDATAllotment from "./IAMDATAllotment";
import IAMDATAllotmentDialog from "./IAMDATAllotmentDialog";
import StarsCalendar from "./StarsCalendar";

const ManageIAMDATAllotments = (props) => {
  const [isAllotmentDialogOpen, setIsAllotmentDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [isRecalculateDialogOpen, setIsRecalculateDialogOpen] = useState(false);
  const [isRecalculateLoading, setIsRecalculateLoading] = useState(false);
  const [isRecalculateErrorDialogOpen, setIsRecalculateErrorDialogOpen] = useState(false);
  const [recalculateError, setRecalculateError] = useState("");
  const [isOverrideDialogOpen, setIsOverrideDialogOpen] = useState(false);
  const [allotmentErrors, setAllotmentErrors] = useState("");

  useEffect(() => {
    if (props.schedMonth !== "" && props.schedYear !== "") {
      setAllotmentErrors(
        validateAllotments(
          props.bidGroup,
          getTotalAllotments(props.allotmentDayState),
          props.calcDailyAllotments,
          props.schedMonth,
          props.schedYear
        )
      );
    }
  }, [props.allotmentDayState, props.calcDailyAllotments, props.bidGroup, props.schedMonth, props.schedYear]);

  const handleAllotmentDialogOpen = (dayState) => {
    const dateObject = convertIsoDateToLuxon(dayState.date);
    const converted_year = parseInt(props.schedYear);
    const thanksgivingDate = getThanksGivingDateOfTheYear(converted_year);

    // do not open allotment dialog on thanksgiving and christmas dates
    if (dayState.date && dayState.amOrPmCount >= 0 && !isHoliday(dateObject, converted_year, thanksgivingDate)) {
      setSelectedDate(dayState.date);
      setIsAllotmentDialogOpen(true);
    }
  };

  const handleRecalculateDialogRecalculateClick = () => {
    setIsRecalculateLoading(true);
    fetchAllotmentValueByStationWorkgroupAndYear(props.bid.station, props.bid.workgroup, props.schedYear)
      .then((res) => res.json())
      .then((response) => {
        if (response?.pk) {
          const calcDailyAllotments = {
            amOrPmCount: response.allotmentValue,
          };
          props.setCalcDailyAllotments(calcDailyAllotments);
          props.setAllotmentDayState(
            getDayStateForIAMDATAllotment(props.schedYear, props.schedMonth, calcDailyAllotments)
          );
        } else {
          // when allotmentValue is not returned from the api default to 1. (this will useful for 2025 or any year when calc is not done yet)
          const calcDailyAllotments = {
            amOrPmCount: 1,
          };
          props.setCalcDailyAllotments(calcDailyAllotments);
          props.setAllotmentDayState(
            getDayStateForIAMDATAllotment(props.schedYear, props.schedMonth, calcDailyAllotments)
          );
        }
        props.setIsSchedMonthOrYearChanged(false);
        setIsRecalculateDialogOpen(false);
        setIsRecalculateLoading(false);
      })
      .catch((err) => {
        console.error("error", err);
        props.setCalcDailyAllotments(calcDailyAllotmentInitialState);
        props.setAllotmentDayState([]);

        setRecalculateError("Error. Something went wrong");
        setIsRecalculateErrorDialogOpen(true);

        setIsRecalculateDialogOpen(false);
        setIsRecalculateLoading(false);
      });
  };

  const renderBidEffectiveDatesChanged = () => {
    return (
      <Grid item xs={12}>
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          The Bid Effective Month/Year has changed. Please click Recalculate to update the allotments.
        </Alert>
      </Grid>
    );
  };

  const renderBidGroupAndAllotmentsCountsMismatch = () => {
    return (
      <Grid item xs={12}>
        <Alert severity={isClosedBid(props.bid.status) ? "error" : "warning"} sx={{ borderRadius: 0 }}>
          {"The minimum number of daily allotments has changed. Please update the allotments by clicking Recalculate."}
        </Alert>
      </Grid>
    );
  };

  return (
    <>
      {props.isBidAllotmentsLoading || props.isBidGroupLoading ? (
        <PageLoading />
      ) : (
        <div data-testid="manage-iam-dat-allotments">
          {!!validateAllotments(
            props.bidGroup,
            getTotalAllotments(props.allotmentDayState),
            props.calcDailyAllotments,
            props.schedMonth,
            props.schedYear
          ) && renderBidGroupAndAllotmentsCountsMismatch()}
          {props.isSchedMonthOrYearChanged && renderBidEffectiveDatesChanged()}
          <Grid container sx={{ border: "solid 1px", borderColor: "secondary.swaGray2" }}>
            <Grid container sx={{ padding: "10px" }}>
              <Grid item xs={8}>
                <Typography
                  variant="h6"
                  sx={{ textAlign: "left", marginTop: "10px", marginLeft: "10px", fontWeight: "bold" }}
                >
                  {props.bid.station} | {props.bid.workgroup}
                  {props.schedMonth !== "" &&
                    props.schedYear !== "" &&
                    ` | ${props.schedYear} ${props.schedMonth.substr(0, 3)}`}
                  {props.bidName ? ` | ${props.bidName}` : ""}
                </Typography>
              </Grid>
              <Grid
                item
                xs={4}
                container
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "right",
                  paddingRight: "1rem",
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  size="medium"
                  sx={{
                    marginRight: "10px",
                  }}
                  onClick={() => {
                    setIsOverrideDialogOpen(true);
                  }}
                  disabled={
                    props.schedMonth === "" ||
                    props.schedYear === "" ||
                    props.bidGroup.length === 0 ||
                    isAwardedBid(props.bid.status) ||
                    isDeletedBid(props.bid.status)
                  }
                >
                  Override
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  size="medium"
                  onClick={() => {
                    setIsRecalculateDialogOpen(true);
                  }}
                  disabled={
                    props.schedMonth === "" ||
                    props.schedYear === "" ||
                    props.bidGroup.length === 0 ||
                    isAwardedBid(props.bid.status) ||
                    isDeletedBid(props.bid.status)
                  }
                >
                  Recalculate
                </Button>
              </Grid>
            </Grid>
            <>
              <Grid container sx={{ backgroundColor: "secondary.swaLightestBlue" }}>
                <Grid item xs={6}>
                  <Grid container spacing={2} sx={{ padding: "10px" }}>
                    <Grid item>Total Employees: </Grid>
                    <Grid item sx={{ color: allotmentErrors ? "primary.swaRed" : "", fontWeight: "bold" }}>
                      {getEligibleEmployees(props.bidGroup).length + getIneligibleEmployees(props.bidGroup).length}
                    </Grid>
                    <Grid item>Total Allotments:</Grid>

                    <Grid item sx={{ color: allotmentErrors ? "primary.swaRed" : "", fontWeight: "bold" }}>
                      {getTotalAllotments(props.allotmentDayState)}
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={6}>
                  <Grid container spacing={2} justifyContent="flex-end" sx={{ padding: "10px" }}>
                    <Grid item> Calculated Daily Allotments: </Grid>
                    <Grid item sx={{ fontWeight: "bold" }}>
                      {isObjectEmpty(props.allotmentDayState) ? "NA" : props.calcDailyAllotments.amOrPmCount}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              {props.bidGroup?.length === 0 || props.schedMonth === "" || props.schedYear === "" ? (
                <Grid
                  container
                  sx={{ minHeight: "280px", color: "secondary.swaBlue", padding: "1rem", textAlign: "left" }}
                >
                  <Grid item xs={12} sx={{ color: "primary.swaBlue" }}>
                    <Grid>
                      <Typography sx={{ fontWeight: "bold" }}>
                        Please select the IAM DAT bid month + year and load the Bid Group to view the allotments.
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              ) : (
                <Grid container>
                  <StarsCalendar
                    dayState={props.allotmentDayState}
                    DayComponent={IAMDATAllotment}
                    handleDialogOpen={handleAllotmentDialogOpen}
                    isLoading={props.isBidAllotmentsLoading || props.isBidGroupLoading}
                  />
                </Grid>
              )}
            </>
          </Grid>
          <Grid sx={{ border: "40px" }}>
            <IAMDATAllotmentDialog
              isAllotmentDialogOpen={isAllotmentDialogOpen}
              allotmentDayState={props.allotmentDayState}
              date={selectedDate}
              setAllotmentDayState={props.setAllotmentDayState}
              setIsAllotmentDialogOpen={setIsAllotmentDialogOpen}
              setCalcDailyAllotments={props.setCalcDailyAllotments}
              calcDailyAllotments={props.calcDailyAllotments}
              bid={props.bid}
              schedMonth={props.schedMonth}
              schedYear={props.schedYear}
            />
          </Grid>
          {isRecalculateDialogOpen && (
            <ConfirmationDialog
              isDialogOpen={isRecalculateDialogOpen}
              title={"Recalculate Allotments"}
              message={""}
              hasWarning={true}
              warningMessage={"This will override any edits made to the allotments."}
              yesTitle={"Recalculate"}
              noTitle={"Cancel"}
              handleYesButtonClicked={handleRecalculateDialogRecalculateClick}
              handleNoButtonClicked={() => {
                setIsRecalculateDialogOpen(false);
              }}
              isYesLoading={isRecalculateLoading}
            />
          )}
          {isRecalculateErrorDialogOpen && (
            <SuccessDialog
              isSuccessDialogOpen={isRecalculateErrorDialogOpen}
              title="Recalculate Error"
              hasError={true}
              error={recalculateError}
              handleOKClick={() => {
                setIsRecalculateErrorDialogOpen(false);
              }}
            />
          )}
          {isOverrideDialogOpen && (
            <AllotmentsOverrideDialog
              isDialogOpen={isOverrideDialogOpen}
              onClickDialogClose={() => setIsOverrideDialogOpen(false)}
              allotmentDayState={props.allotmentDayState}
              setAllotmentDayState={props.setAllotmentDayState}
              schedMonth={props.schedMonth}
              schedYear={props.schedYear}
              calcDailyAllotments={props.calcDailyAllotments}
            />
          )}
        </div>
      )}
    </>
  );
};

ManageIAMDATAllotments.propTypes = {
  bid: PropTypes.object.isRequired,
  bidGroup: PropTypes.array.isRequired,
  schedMonth: PropTypes.string.isRequired,
  schedYear: PropTypes.string.isRequired,
  isBidAllotmentsLoading: PropTypes.bool.isRequired,
  isBidGroupLoading: PropTypes.bool.isRequired,
  calcDailyAllotments: PropTypes.object.isRequired,
  setCalcDailyAllotments: PropTypes.func.isRequired,
  allotmentDayState: PropTypes.array.isRequired,
  setAllotmentDayState: PropTypes.func.isRequired,
  isSchedMonthOrYearChanged: PropTypes.bool.isRequired,
  setIsSchedMonthOrYearChanged: PropTypes.func.isRequired,
  bidName: PropTypes.string,
};

export default ManageIAMDATAllotments;
